/**
 * schema-registry.ts
 *
 * Versioned Schema Extension Registry, Audience-Specific Redaction Engine,
 * and Bounded Variant Combinatorial Generator for Sovereign Commerce.
 */

import type {
  SectorType,
  SchemaExtension,
  FoodSchemaExtension,
  FashionSchemaExtension,
  ElectronicsSchemaExtension,
  PharmaSchemaExtension,
  DigitalSchemaExtension,
  ServiceSchemaExtension,
  CustomSchemaExtension,
  ProjectionAudience,
  ProductOption,
} from './commerce-contracts.js';

export interface ValidationResult {
  readonly valid: boolean;
  readonly errors: readonly string[];
}

export interface SchemaExtensionDefinition<T = any> {
  readonly extensionId: string;
  readonly sector: SectorType;
  readonly version: string;
  readonly appliesTo: 'product' | 'variant' | 'batch';
  validate(value: unknown): ValidationResult;
  serialize(value: T): unknown;
  redact(value: T, audience: ProjectionAudience): unknown;
}

// ── 1. Sector Definitions ───────────────────────────────────────────────────

export const foodExtensionV1: SchemaExtensionDefinition<FoodSchemaExtension> = {
  extensionId: 'food.v1',
  sector: 'food',
  version: '1.0.0',
  appliesTo: 'product',
  validate(val: unknown): ValidationResult {
    const errors: string[] = [];
    if (!val || typeof val !== 'object') {
      return { valid: false, errors: ['Value must be a valid object'] };
    }
    const ext = val as Partial<FoodSchemaExtension>;
    if (!['high', 'medium', 'low'].includes(ext.perishability as string)) {
      errors.push(`Invalid food perishability: ${ext.perishability}`);
    }
    if (ext.storageTempCelsius) {
      if (typeof ext.storageTempCelsius !== 'object') {
        errors.push('storageTempCelsius must be an object');
      }
    }
    if (ext.allergens && !Array.isArray(ext.allergens)) {
      errors.push('allergens must be an array of string allergen codes');
    }
    return { valid: errors.length === 0, errors };
  },
  serialize(val: FoodSchemaExtension) {
    return { ...val };
  },
  redact(val: FoodSchemaExtension, audience: ProjectionAudience) {
    if (audience === 'public' || audience === 'kiosk') {
      const { storageTempCelsius, ...publicSafe } = val;
      return publicSafe;
    }
    return val;
  },
};

export const fashionExtensionV1: SchemaExtensionDefinition<FashionSchemaExtension> = {
  extensionId: 'fashion.v1',
  sector: 'fashion',
  version: '1.0.0',
  appliesTo: 'product',
  validate(val: unknown): ValidationResult {
    const errors: string[] = [];
    if (!val || typeof val !== 'object') {
      return { valid: false, errors: ['Value must be a valid object'] };
    }
    const ext = val as Partial<FashionSchemaExtension>;
    if (!ext.size || typeof ext.size !== 'string' || !ext.color || typeof ext.color !== 'string') {
      errors.push('Fashion schema extension requires both "size" and "color"');
    }
    return { valid: errors.length === 0, errors };
  },
  serialize(val: FashionSchemaExtension) {
    return { ...val };
  },
  redact(val: FashionSchemaExtension, _audience: ProjectionAudience) {
    return val;
  },
};

export const electronicsExtensionV1: SchemaExtensionDefinition<ElectronicsSchemaExtension> = {
  extensionId: 'electronics.v1',
  sector: 'electronics',
  version: '1.0.0',
  appliesTo: 'product',
  validate(val: unknown): ValidationResult {
    const errors: string[] = [];
    if (!val || typeof val !== 'object') {
      return { valid: false, errors: ['Value must be a valid object'] };
    }
    const ext = val as Partial<ElectronicsSchemaExtension>;
    if (typeof ext.serialized !== 'boolean') {
      errors.push('Electronics extension requires "serialized" boolean');
    }
    if (typeof ext.warrantyMonths !== 'number' || ext.warrantyMonths < 0) {
      errors.push('Electronics extension requires non-negative "warrantyMonths" number');
    }
    return { valid: errors.length === 0, errors };
  },
  serialize(val: ElectronicsSchemaExtension) {
    return { ...val };
  },
  redact(val: ElectronicsSchemaExtension, audience: ProjectionAudience) {
    if (audience === 'public' || audience === 'kiosk') {
      const { regulatoryCertifications, ...publicSafe } = val;
      return publicSafe;
    }
    return val;
  },
};

export const pharmaExtensionV1: SchemaExtensionDefinition<PharmaSchemaExtension> = {
  extensionId: 'pharma.v1',
  sector: 'pharma',
  version: '1.0.0',
  appliesTo: 'product',
  validate(val: unknown): ValidationResult {
    const errors: string[] = [];
    if (!val || typeof val !== 'object') {
      return { valid: false, errors: ['Value must be a valid object'] };
    }
    const ext = val as Partial<PharmaSchemaExtension>;
    if (typeof ext.prescriptionRequired !== 'boolean') {
      errors.push('Pharma extension requires "prescriptionRequired" boolean');
    }
    if (!Array.isArray(ext.activeSubstances) || ext.activeSubstances.length === 0) {
      errors.push('Pharma extension requires "activeSubstances" array with at least one entry');
    }
    return { valid: errors.length === 0, errors };
  },
  serialize(val: PharmaSchemaExtension) {
    return { ...val };
  },
  redact(val: PharmaSchemaExtension, audience: ProjectionAudience) {
    if (audience === 'public' || audience === 'kiosk' || audience === 'pos') {
      const { controlledSubstanceSchedule, ...safe } = val;
      return safe;
    }
    return val;
  },
};

export const digitalExtensionV1: SchemaExtensionDefinition<DigitalSchemaExtension> = {
  extensionId: 'digital.v1',
  sector: 'digital',
  version: '1.0.0',
  appliesTo: 'product',
  validate(val: unknown): ValidationResult {
    const errors: string[] = [];
    if (!val || typeof val !== 'object') {
      return { valid: false, errors: ['Value must be a valid object'] };
    }
    const ext = val as Partial<DigitalSchemaExtension>;
    if (!['perpetual', 'subscription', 'single-use'].includes(ext.licenseType as string)) {
      errors.push(`Invalid digital license type: ${ext.licenseType}`);
    }
    if (ext.maxActivations !== undefined && (typeof ext.maxActivations !== 'number' || ext.maxActivations < 1)) {
      errors.push('maxActivations must be a positive integer');
    }
    if (ext.deliveryMethod && !['license_key', 'file_download', 'external_url'].includes(ext.deliveryMethod)) {
      errors.push(`Invalid delivery method: ${ext.deliveryMethod}`);
    }
    return { valid: errors.length === 0, errors };
  },
  serialize(val: DigitalSchemaExtension) {
    return { ...val };
  },
  redact(val: DigitalSchemaExtension, audience: ProjectionAudience) {
    if (audience === 'public') {
      const { downloadAssetMediaId, downloadRef, ...publicSafe } = val;
      return publicSafe;
    }
    return val;
  },
};

export const serviceExtensionV1: SchemaExtensionDefinition<ServiceSchemaExtension> = {
  extensionId: 'service.v1',
  sector: 'service',
  version: '1.0.0',
  appliesTo: 'product',
  validate(val: unknown): ValidationResult {
    const errors: string[] = [];
    if (!val || typeof val !== 'object') {
      return { valid: false, errors: ['Value must be a valid object'] };
    }
    const ext = val as Partial<ServiceSchemaExtension>;
    if (ext.durationMinutes !== undefined && (typeof ext.durationMinutes !== 'number' || ext.durationMinutes <= 0)) {
      errors.push('durationMinutes must be a positive number if provided');
    }
    if (ext.locationRequirement && !['remote', 'on-site', 'in-store'].includes(ext.locationRequirement)) {
      errors.push(`Invalid locationRequirement: ${ext.locationRequirement}`);
    }
    if (ext.serviceType && !['consultation', 'repair', 'workshop', 'class'].includes(ext.serviceType)) {
      errors.push(`Invalid serviceType: ${ext.serviceType}`);
    }
    return { valid: errors.length === 0, errors };
  },
  serialize(val: ServiceSchemaExtension) {
    return { ...val };
  },
  redact(val: ServiceSchemaExtension, audience: ProjectionAudience) {
    if (audience === 'public' || audience === 'kiosk') {
      const { capacityResourceId, ...safe } = val;
      return safe;
    }
    return val;
  },
};

export const customExtensionV1: SchemaExtensionDefinition<CustomSchemaExtension> = {
  extensionId: 'custom.v1',
  sector: 'custom',
  version: '1.0.0',
  appliesTo: 'product',
  validate(val: unknown): ValidationResult {
    const errors: string[] = [];
    if (!val || typeof val !== 'object') {
      return { valid: false, errors: ['Value must be a valid object'] };
    }
    const ext = val as Partial<CustomSchemaExtension>;
    if (!ext.schemaVersion || typeof ext.attributes !== 'object' || ext.attributes === null) {
      errors.push('Custom schema extension requires "schemaVersion" and an "attributes" record');
    }
    return { valid: errors.length === 0, errors };
  },
  serialize(val: CustomSchemaExtension) {
    return { ...val };
  },
  redact(val: CustomSchemaExtension, _audience: ProjectionAudience) {
    return val;
  },
};

// ── 2. Schema Registry Class ────────────────────────────────────────────────

export class SchemaExtensionRegistry {
  private definitions = new Map<string, SchemaExtensionDefinition>();

  constructor() {
    this.register(foodExtensionV1);
    this.register(fashionExtensionV1);
    this.register(electronicsExtensionV1);
    this.register(pharmaExtensionV1);
    this.register(digitalExtensionV1);
    this.register(serviceExtensionV1);
    this.register(customExtensionV1);
  }

  public register(definition: SchemaExtensionDefinition): void {
    const key = `${definition.sector}.${definition.version}`;
    this.definitions.set(key, definition);
    this.definitions.set(definition.extensionId, definition);
    this.definitions.set(definition.sector, definition);
  }

  public get(idOrSector: string, version = '1.0.0'): SchemaExtensionDefinition | undefined {
    const versionedKey = `${idOrSector}.${version}`;
    return this.definitions.get(versionedKey) || this.definitions.get(idOrSector);
  }

  public validateExtension(ext?: SchemaExtension): ValidationResult {
    if (!ext) return { valid: true, errors: [] };
    const def = this.get(ext.sector, ext.schemaVersion);
    if (!def) {
      return { valid: false, errors: [`Unknown schema extension sector or version: ${ext.sector}`] };
    }
    return def.validate(ext);
  }

  public redactForAudience(ext: SchemaExtension | undefined, audience: ProjectionAudience): unknown {
    if (!ext) return undefined;
    const def = this.get(ext.sector, ext.schemaVersion);
    if (!def) return ext;
    return def.redact(ext, audience);
  }
}

export const globalSchemaRegistry = new SchemaExtensionRegistry();

// ── 3. Bounded Variant Combinatorial Generator ──────────────────────────────

export const MAX_ALLOWED_VARIANTS = 100;
export const MAX_ALLOWED_OPTIONS = 4;
export const MAX_VALUES_PER_OPTION = 12;

export interface VariantCombinationEstimate {
  readonly status: 'ok' | 'too-large' | 'empty' | 'exceeds-options' | 'exceeds-values';
  readonly count: number;
  readonly message?: string;
}

export interface GeneratedVariantDraft {
  readonly combinationKey: string;
  readonly skuSuffix: string;
  readonly attributes: Record<string, string>;
  readonly extraPriceMinor?: string;
}

export interface VariantGenerationResult {
  readonly success: boolean;
  readonly variants: readonly GeneratedVariantDraft[];
  readonly error?: string;
}

/**
 * Normalizes attribute keys and values into a stable, sorted combination key.
 * e.g. color=Navy|size=L
 */
export function buildStableCombinationKey(attributes: Record<string, string>): string {
  const sortedKeys = Object.keys(attributes).sort();
  return sortedKeys
    .map((k) => `${k.toLowerCase().trim()}=${attributes[k].toLowerCase().trim()}`)
    .join('|');
}

/**
 * Pre-materialization estimator to guard against Cartesian product combinatorial explosion.
 */
export function estimateVariantCount(options: readonly ProductOption[]): VariantCombinationEstimate {
  if (!options || options.length === 0) {
    return { status: 'empty', count: 0, message: 'No option axes defined' };
  }

  if (options.length > MAX_ALLOWED_OPTIONS) {
    return {
      status: 'exceeds-options',
      count: 0,
      message: `Option axes count (${options.length}) exceeds maximum limit (${MAX_ALLOWED_OPTIONS})`,
    };
  }

  let totalCombinations = 1;
  for (const opt of options) {
    const valCount = opt.values.length;
    if (valCount === 0) continue;
    if (valCount > MAX_VALUES_PER_OPTION) {
      return {
        status: 'exceeds-values',
        count: 0,
        message: `Option "${Object.values(opt.name)[0] || opt.optionId}" has ${valCount} values (max is ${MAX_VALUES_PER_OPTION})`,
      };
    }
    totalCombinations *= valCount;
  }

  if (totalCombinations > MAX_ALLOWED_VARIANTS) {
    return {
      status: 'too-large',
      count: totalCombinations,
      message: `Combinatorial matrix yields ${totalCombinations} variants, exceeding safety limit of ${MAX_ALLOWED_VARIANTS}`,
    };
  }

  return { status: 'ok', count: totalCombinations };
}

/**
 * Generates bounded Cartesian product variants safely with stable combination keys.
 */
export function generateVariantCombinations(
  options: readonly ProductOption[],
  baseSkuPrefix = 'SKU'
): VariantGenerationResult {
  const estimate = estimateVariantCount(options);
  if (estimate.status !== 'ok') {
    return { success: false, variants: [], error: estimate.message };
  }

  const activeOptions = options.filter((o) => o.values.length > 0);
  if (activeOptions.length === 0) {
    return { success: true, variants: [] };
  }

  let currentDrafts: Array<{ attributes: Record<string, string>; extraPriceMinor?: string; skuParts: string[] }> = [
    { attributes: {}, extraPriceMinor: undefined, skuParts: [] },
  ];

  for (const opt of activeOptions) {
    const optName = Object.values(opt.name)[0] || opt.optionId;
    const nextDrafts: typeof currentDrafts = [];

    for (const draft of currentDrafts) {
      for (const val of opt.values) {
        const valLabel = Object.values(val.label)[0] || val.valueId;
        const cleanValPart = valLabel.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 4);

        nextDrafts.push({
          attributes: {
            ...draft.attributes,
            [optName]: valLabel,
          },
          extraPriceMinor: val.extraPriceMinor || draft.extraPriceMinor,
          skuParts: [...draft.skuParts, cleanValPart],
        });
      }
    }
    currentDrafts = nextDrafts;
  }

  // Deduplicate and construct stable drafts
  const seenKeys = new Set<string>();
  const results: GeneratedVariantDraft[] = [];

  for (const d of currentDrafts) {
    const combinationKey = buildStableCombinationKey(d.attributes);
    if (seenKeys.has(combinationKey)) continue;
    seenKeys.add(combinationKey);

    const skuSuffix = d.skuParts.join('-');
    results.push({
      combinationKey,
      skuSuffix: skuSuffix ? `${baseSkuPrefix}-${skuSuffix}` : baseSkuPrefix,
      attributes: d.attributes,
      extraPriceMinor: d.extraPriceMinor,
    });
  }

  return {
    success: true,
    variants: results,
  };
}
